import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Globe, MapPin, Building} from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const SupplierModal = ({ supplier, type, onClose, onSave, lang }) => {
  const { t } = useTranslation(lang);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (supplier && type === 'edit') {
      setFormData({
        name: supplier.name || '',
        providerType: supplier.providerType || 'company',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        website: supplier.website || '',
        currency: supplier.currency || 'EUR'
      });
    } else {
      setFormData({
        name: '',
        providerType: 'company',
        email: '',
        phone: '',
        address: '',
        website: '',
        currency: 'EUR',
        charges: '',
        password: '',
        confirmPassword: ''
      });
    }
  }, [supplier, type]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePhoneChange = (value) => {
    // Permettre uniquement les chiffres, espaces, +, (), -
    const cleanedValue = value.replace(/[^\d\s\+\-\(\)]/g, '');
    handleChange('phone', cleanedValue);
  };

  // Fonction pour gérer le changement des frais (uniquement des chiffres et %)
  const handleChargesChange = (value) => {
    // Permettre uniquement les chiffres, point décimal et %
    const cleanedValue = value.replace(/[^\d.%]/g, '');
    handleChange('charges', cleanedValue);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    }

    // Validation du téléphone (optionnel, mais si rempli, doit contenir au moins des chiffres)
    if (formData.phone.trim() && !/[\d]/.test(formData.phone)) {
      newErrors.phone = t('invalidPhone');
    }

    if (formData.address.length < 5) {
      newErrors.address = t('addressTooShort');
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    const errorMessages = Object.values(errors).join(', ');
    toast.error(`Veuillez corriger: ${errorMessages}`);
    return;
  }

  const supplierData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    address: formData.address
  };

  try {
    const response = await api.post(`/providers/${supplier.id}/update`, supplierData);
    
    // Succès
    onSave(supplierData);
    
  } catch (error) {

    let errorMessage = 'Une erreur est survenue';
    
    if (error.response?.data?.request?.message) {
      errorMessage = error.response.data.request.message;
    }
    else if (error.response?.data?.result?.Error?.message) {
      errorMessage = error.response.data.result.Error.message;
    }
    else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    else if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      errorMessage = Object.values(errors)
        .flat()
        .join(', ');
    }
    else if (!error.response) {
      errorMessage = 'Erreur de connexion. Vérifiez votre réseau.';
    }
    
    // Afficher avec toast
    toast.error(errorMessage);
  }
};

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      background: 'white',
      borderRadius: '12px',
      width: '100%',
      maxWidth: '800px',
      minWidth: '300px',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '24px',
      borderBottom: '1px solid #e5e7eb'
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '4px',
      color: '#6b7280'
    },
    content: {
      padding: '24px'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px'
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151'
    },
    input: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    inputError: {
      borderColor: '#ef4444'
    },
    inputDisabled: {
      backgroundColor: '#f9fafb',
      color: '#6b7280',
      cursor: 'not-allowed'
    },
    select: {
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      backgroundColor: 'white'
    },
    passwordContainer: {
      position: 'relative'
    },
    passwordToggle: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#6b7280'
    },
    error: {
      color: '#ef4444',
      fontSize: '12px',
      marginTop: '4px'
    },
    helpText: {
      color: '#6b7280',
      fontSize: '12px',
      marginTop: '4px',
      fontStyle: 'italic'
    },
    footer: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
      padding: '24px',
      borderTop: '1px solid #e5e7eb'
    },
    button: {
      padding: '10px 20px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonPrimary: {
      backgroundColor: '#7c3aed',
      color: 'white'
    },
    buttonSecondary: {
      backgroundColor: '#6b7280',
      color: 'white'
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>
            <Building size={20} />
            {type === 'add' && t('addSupplier')}
            {type === 'edit' && t('editSupplier')}
          </div>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.grid}>
              {/* Nom */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <User size={16} />
                  {t('name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.name && styles.inputError)
                  }}
                  placeholder={t('enterName')}
                />
                {errors.name && <div style={styles.error}>{errors.name}</div>}
              </div>

              {/* Type de fournisseur */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Building size={16} />
                  {t('providerType')}
                </label>
                <select
                  value={formData.providerType}
                  style={styles.select}
                  disabled
                >
                  <option value="company">{t('company')}</option>
                  <option value="individual">{t('individual')}</option>
                </select>
              </div>

              {/* Email */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Mail size={16} />
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.email && styles.inputError)
                  }}
                  placeholder={t('enterEmail')}
                />
                {errors.email && <div style={styles.error}>{errors.email}</div>}
              </div>

              {/* Téléphone */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Phone size={16} />
                  {t('phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  maxLength={12}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.phone && styles.inputError)
                  }}
                  placeholder={t('enterPhone')}
                />
                {errors.phone && <div style={styles.error}>{errors.phone}</div>}
                <div style={styles.helpText}>{t('phoneExample')}</div>
              </div>

                          {/* Adresse (pleine largeur) */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <MapPin size={16} />
                {t('address')}
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                style={styles.input}
                placeholder={t('enterAddress')}
              />
            </div>

              {/* Site web */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  <Globe size={16} />
                  {t('website')}
                </label>
<input
  type="url"
  value={formData.website || ''} 
  style={styles.input}
  placeholder={t('enterWebsite')}
  disabled
/>
              </div>
            </div>




          </form>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={onClose}
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={handleSubmit}
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierModal;